class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onBackdropClick.bind(this));
    this.addEventListener('change', this.onDrawerChange.bind(this));
    this.addEventListener('click', this.onDrawerInteraction.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onBackdropClick(event) {
    if (event.target === this || event.target.id === 'CartDrawer-Overlay') {
      this.close();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.syncDrawerHistory(true);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.syncDrawerHistory(false);
  }

  static isTouchNav() {
    return window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)').matches;
  }

  syncDrawerHistory(isOpen) {
    if (!CartDrawer.isTouchNav()) return;

    if (isOpen) {
      if (this.ncDrawerHist) return;
      if (!(history.state && history.state.ncCartDrawer)) {
        history.pushState(
          Object.assign({}, history.state || {}, { ncCartDrawer: true }),
          '',
          location.pathname + location.search + '#nc-cart-drawer'
        );
      }
      this.ncDrawerHist = true;
      return;
    }

    if (!this.ncDrawerHist) return;
    this.ncDrawerHist = false;
    if (this.ncDrawerFromPop) {
      this.ncDrawerFromPop = false;
      return;
    }
    if (history.state && history.state.ncCartDrawer) history.back();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const usable =
      typeof window.ncBxgyCartStateUsable === 'function'
        ? window.ncBxgyCartStateUsable(parsedState)
        : typeof parsedState?.item_count === 'number';
    if (usable) {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    if (!usable && !this.querySelector('.drawer__inner-empty')) {
      this.classList.remove('is-empty');
    }

    setTimeout(() => this.open());
  }

  onDrawerChange(event) {
    const select = event.target.closest('.cart-item__variant-select');
    if (!select) return;

    const line = select.dataset.line || select.dataset.index;
    const quantityInput = this.querySelector(`#Drawer-quantity-${line}`);
    const quantity = parseInt(quantityInput?.value, 10) || 1;
    const cartItems = this.querySelector('cart-drawer-items');

    if (!line || !cartItems) return;
    cartItems.updateQuantity(line, quantity, event, select.name, select.value);
  }

  onDrawerInteraction(event) {
    const couponButton = event.target.closest('.cart-drawer__coupon-apply');
    if (couponButton) {
      event.preventDefault();
      this.applyCoupon();
      return;
    }

    const addButton = event.target.closest('.cart-drawer__fbt-add');
    if (addButton) {
      event.preventDefault();
      this.addFrequentlyBoughtProduct(addButton);
    }
  }

  applyCoupon() {
    const input = this.querySelector('.cart-drawer__coupon-input');
    const message = this.querySelector('.cart-drawer__coupon-message');
    if (!input || !message) return;

    const code = input.value.trim();
    if (!code) {
      message.textContent = 'Please enter a promo code.';
      message.classList.remove('success');
      message.classList.add('error');
      return;
    }

    if (window.ncIsFirstOrderBlocked?.(code)) {
      message.textContent = 'You are not eligible for this code. Please try using another code.';
      message.classList.remove('success');
      message.classList.add('error');
      return;
    }

    const body = JSON.stringify({
      discount: code,
      sections: this.getSectionsToRender().map(({ id }) => id),
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_update_url, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || 'Unable to apply promo code.');

        const entry = (state.discount_codes || []).find(
          (d) => d.code?.toUpperCase() === code.toUpperCase()
        );
        if (!entry?.applicable) {
          throw new Error(
            entry
              ? 'You are not eligible for this code. Please try using another code.'
              : 'Enter a valid discount code.'
          );
        }

        this.getSectionsToRender().forEach((section) => {
          const el = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!el || !state.sections?.[section.id]) return;
          el.innerHTML = this.getSectionInnerHTML(state.sections[section.id], section.selector);
        });

        const msg = this.querySelector('.cart-drawer__coupon-message');
        const inp = this.querySelector('.cart-drawer__coupon-input');
        if (inp) inp.value = code;
        if (msg) {
          msg.textContent = 'Promo code applied.';
          msg.classList.remove('error');
          msg.classList.add('success');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-coupon', cartData: state });
      })
      .catch((error) => {
        message.textContent = error.message || 'Unable to apply promo code. Please try again.';
        message.classList.remove('success');
        message.classList.add('error');
      });
  }

  addFrequentlyBoughtProduct(button) {
    const variantId = button.dataset.variantId;
    const message = button.closest('.cart-drawer__fbt-card')?.querySelector('.cart-drawer__fbt-status');
    if (!variantId) return;

    button.setAttribute('aria-disabled', 'true');
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    this.getSectionsToRender().forEach((section) => {
      formData.append('sections[]', section.id);
    });
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (message) {
            message.textContent = response.description || 'Could not add selected product.';
            message.classList.add('error');
          }
          return;
        }

        if (message) {
          message.textContent = 'Added to cart.';
          message.classList.remove('error');
          message.classList.add('success');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-fbt',
          productVariantId: variantId,
          cartData: response,
        }).then(() => {
          this.renderContents(response);
        });
      })
      .catch(() => {
        if (message) {
          message.textContent = 'Could not add selected product.';
          message.classList.add('error');
        }
      })
      .finally(() => {
        button.removeAttribute('aria-disabled');
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.addEventListener('popstate', function () {
  var drawer = document.querySelector('cart-drawer');
  if (!drawer || !drawer.ncDrawerHist) return;
  drawer.ncDrawerFromPop = true;
  drawer.close();
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
